import { useTranslation } from "react-i18next";
import { Import, Calendar2 } from "iconsax-react";
import { Button, DatePicker } from "@/shared/ui";
import type { RangeValue } from "@/shared/ui";
import { getMonthName, type Locale } from "@/shared/utils";

interface Props {
  onExport: () => void;
  isExporting: boolean;
  disabled?: boolean;
  dateRange: RangeValue;
  onDateRangeChange: (v: RangeValue | null) => void;
  locale: Locale;
}

const YEAR_SUFFIX: Record<Locale, string> = { ru: "год", kk: "жыл", en: "" };

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function formatRangeDisplay(range: RangeValue, locale: Locale): string {
  const { start, end } = range;
  if (!start || !end) return "";

  const sy = start.getFullYear();
  const sm = start.getMonth() + 1;
  const sd = start.getDate();
  const ey = end.getFullYear();
  const em = end.getMonth() + 1;
  const ed = end.getDate();

  if (sy === ey && sm === 1 && sd === 1 && em === 12 && ed === 31) {
    const suffix = YEAR_SUFFIX[locale];
    return suffix ? `${sy} ${suffix}` : `${sy}`;
  }

  if (sy === ey && sm === em && sd === 1 && ed === lastDayOfMonth(ey, em)) {
    return `${getMonthName(sm, locale)} ${sy}`;
  }

  return `${pad(sd)}.${pad(sm)}.${sy} — ${pad(ed)}.${pad(em)}.${ey}`;
}

export default function ProjectTableHeader({
  onExport,
  isExporting,
  disabled,
  dateRange,
  onDateRangeChange,
  locale,
}: Props) {
  const { t } = useTranslation("ProjectTablePage");

  return (
    <div className="flex items-center justify-between pt-1 pb-4 border-b surface-component-stroke mb-5 gap-4">
      <h1 className="text-display-xs content-base-primary">{t("header.title")}</h1>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="lg"
          onClick={onExport}
          disabled={disabled || isExporting}
          className="w-8! h-8! p-0! flex rounded-lg! justify-center items-center content-base-secondary">
          <Import size={20} color="currentColor" />
        </Button>
        <DatePicker
          mode="range"
          value={dateRange}
          onChange={onDateRangeChange}
          locale={locale}
          hasPeriodPicker
          clearable
          isSmall
          align="right"
          width="min-w-48"
          placeholder={t("filters.allTime")}
          icon={<Calendar2 size={16} color="currentColor" />}
          formatDisplayValue={(range) => formatRangeDisplay(range, locale)}
        />
      </div>
    </div>
  );
}
